import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DashboardShell } from "@/components/Shell";
import { Users, Plus, Trash2, MapPin, Eye, EyeOff, Check, X } from "lucide-react";

export default function AdminStaff() {
  const [staff, setStaff] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assignments, setAssignments] = useState([]); // [{staff_id, location_id, is_visible}]
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  async function load() {
    const [s, l, a] = await Promise.all([
      api.get("/staff"),
      api.get("/locations"),
      api.get("/admin/assignments"),
    ]);
    setStaff(s.data);
    setLocations(l.data);
    setAssignments(a.data);
  }
  useEffect(() => { load(); }, []);

  function findAssign(staffId, locationId) {
    return assignments.find((a) => a.staff_id === staffId && a.location_id === locationId);
  }

  async function createStaff() {
    if (!form.name || !form.email || !form.password) return;
    await api.post("/admin/staff", { ...form, role: "staff" });
    setForm({ name: "", email: "", password: "" });
    setCreating(false);
    load();
  }

  async function deleteStaff(id) {
    if (!window.confirm("Mitarbeiter wirklich löschen?")) return;
    await api.delete(`/admin/staff/${id}`);
    load();
  }

  async function toggleAssign(staffId, locationId) {
    const has = findAssign(staffId, locationId);
    if (has) await api.delete(`/locations/${locationId}/staff/${staffId}`);
    else await api.post(`/locations/${locationId}/staff`, { staff_id: staffId, is_visible: true });
    load();
  }

  async function toggleVisibility(staffId, locationId, current) {
    await api.put(`/locations/${locationId}/staff/${staffId}/visibility`, { is_visible: !current });
    load();
  }

  return (
    <DashboardShell role="admin">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-[#EBB300]">Verwaltung</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mt-2 flex items-center gap-3"><Users className="w-9 h-9 text-[#EBB300]" /> Mitarbeiter</h1>
        </div>
        <button onClick={() => setCreating(!creating)} data-testid="admin-add-staff"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#EBB300] text-[#0A0A0C] font-bold hover:bg-[#FAD02C] transition-colors">
          <Plus className="w-4 h-4" /> Neuer Mitarbeiter
        </button>
      </div>

      {creating && (
        <div className="mb-6 bg-[#121216] border border-[#EBB300]/30 rounded-2xl p-6" data-testid="admin-staff-form">
          <div className="grid md:grid-cols-3 gap-4">
            <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-staff-name" />
            <Input label="E-Mail" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="admin-staff-email" type="email" />
            <Input label="Passwort" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="admin-staff-password" type="password" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={createStaff} data-testid="admin-staff-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EBB300] text-[#0A0A0C] font-bold text-sm"><Check className="w-4 h-4" /> Anlegen</button>
            <button onClick={() => setCreating(false)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 text-white text-sm"><X className="w-4 h-4" /> Abbrechen</button>
          </div>
        </div>
      )}

      <div className="space-y-4" data-testid="admin-staff-list">
        {staff.map((s) => (
          <div key={s.id} className="bg-[#121216] border border-white/5 rounded-2xl p-5" data-testid={`admin-staff-${s.id}`}>
            <div className="flex items-start gap-4">
              <img src={s.photo_url || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200"} alt="" className="w-14 h-14 rounded-full object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-bold">{s.name}</div>
                <div className="text-xs text-[#A1A1AA] truncate">{s.bio}</div>
              </div>
              <button onClick={() => deleteStaff(s.id)} data-testid={`admin-delete-staff-${s.id}`}
                className="px-3 py-2 rounded-lg bg-[#EF4444]/10 text-[#EF4444] hover:bg-[#EF4444]/20"><Trash2 className="w-4 h-4" /></button>
            </div>

            <div className="mt-4 pt-4 border-t border-white/5">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] mb-3 flex items-center gap-2"><MapPin className="w-3 h-3" /> Locations & Sichtbarkeit</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {locations.map((loc) => {
                  const a = findAssign(s.id, loc.id);
                  const assigned = !!a;
                  const visible = a?.is_visible;
                  return (
                    <div key={loc.id} className={`flex items-center gap-2 p-3 rounded-lg border ${assigned ? "border-[#EBB300]/30 bg-[#EBB300]/5" : "border-white/5 bg-[#0A0A0C]"}`}>
                      <button onClick={() => toggleAssign(s.id, loc.id)} data-testid={`admin-toggle-${s.id}-${loc.id}`}
                        className="flex-1 text-left text-sm font-semibold truncate">{loc.name}</button>
                      {assigned && (
                        <button onClick={() => toggleVisibility(s.id, loc.id, visible)} data-testid={`admin-visibility-${s.id}-${loc.id}`}
                          className={`p-1.5 rounded-md ${visible ? "text-[#22C55E]" : "text-[#52525B]"}`} title={visible ? "Sichtbar" : "Versteckt"}>
                          {visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}

function Input({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.15em] uppercase text-[#A1A1AA] mb-2 block">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-4 py-2.5 text-white focus:border-[#EBB300]" />
    </label>
  );
}
